/**
 * Spatial work-surface routing for the HQ's purpose-built L1 rooms.
 *
 * This module deliberately owns only Arena-local context: floor position,
 * furniture anchors, prompt copy, and the destination inside the existing
 * workplace phone. Portal continues to own agents, chats, runs, tasks, and
 * teammates without learning anything about rooms or coordinates.
 */
import type {
  WorkplacePhoneDestination,
  WorkplacePhoneTab,
} from '../../store'
import { FLOOR_Y } from './floorPrograms'

export type HqRoomActionId =
  | 'focus-agent'
  | 'huddle-work'
  | 'huddle-people'

export interface HqRoomActionPose {
  actionId: HqRoomActionId
  x: number
  y: number
  z: number
  yawDeg: number
  pitchDeg: number
}

export interface HqRoomAction {
  id: HqRoomActionId
  roomLabel: string
  label: string
  tab: WorkplacePhoneTab
  destination?: WorkplacePhoneDestination
  floor: number
  anchors: readonly (readonly [number, number])[]
  radius: number
}

export const HQ_ROOM_ACTIONS: readonly HqRoomAction[] = [
  {
    id: 'focus-agent',
    roomLabel: 'FOCUS STUDIO',
    label: 'Focus with an agent',
    tab: 'agents',
    floor: 1,
    anchors: [[-20.85, -4.45], [-20.85, -1.45]],
    radius: 1.65,
  },
  {
    id: 'huddle-work',
    roomLabel: 'TEAM HUDDLE',
    label: 'Review team work',
    tab: 'work',
    destination: 'live-work',
    floor: 1,
    anchors: [[-21.6, 3.1]],
    radius: 1.75,
  },
  {
    id: 'huddle-people',
    roomLabel: 'TEAM HUDDLE',
    label: 'Find active teammates',
    tab: 'people',
    floor: 1,
    anchors: [[-23.15, 5.25]],
    radius: 1.25,
  },
]

const PLAYER_EYE_HEIGHT = 1.65
const FLOOR_VERTICAL_TOLERANCE = 1.15

/** Deterministic player poses used by the real-renderer acceptance harness. */
export function hqRoomActionPose(
  actionId: HqRoomActionId,
  islandZ = -1000,
): HqRoomActionPose {
  const poses: Record<HqRoomActionId, Omit<HqRoomActionPose, 'actionId'>> = {
    'focus-agent': {
      x: -20.85, y: FLOOR_Y[1] + 0.1, z: islandZ - 4.45,
      yawDeg: -90, pitchDeg: -4,
    },
    'huddle-work': {
      x: -21.6, y: FLOOR_Y[1] + 0.1, z: islandZ + 3.1,
      yawDeg: 90, pitchDeg: -4,
    },
    'huddle-people': {
      x: -23.15, y: FLOOR_Y[1] + 0.1, z: islandZ + 5.25,
      yawDeg: 0, pitchDeg: -4,
    },
  }
  return { actionId, ...poses[actionId] }
}

/**
 * Return the closest bounded action on the player's current HQ floor.
 * Distance selection keeps adjacent huddle anchors deterministic if their
 * radii ever overlap after a furniture pass.
 */
export function hqRoomActionAt(
  localX: number,
  localY: number,
  localZ: number,
): HqRoomAction | null {
  let nearest: { action: HqRoomAction; distance: number } | null = null
  for (const action of HQ_ROOM_ACTIONS) {
    const floorEyeY = FLOOR_Y[action.floor] + PLAYER_EYE_HEIGHT
    if (Math.abs(localY - floorEyeY) > FLOOR_VERTICAL_TOLERANCE) continue
    for (const [x, z] of action.anchors) {
      const distance = Math.hypot(localX - x, localZ - z)
      if (distance > action.radius) continue
      if (!nearest || distance < nearest.distance) nearest = { action, distance }
    }
  }
  return nearest?.action ?? null
}

export interface HqRoomActionState {
  isPlaying: boolean
  isPaused: boolean
  isAgentTalkOpen: boolean
  isWorkplacePhoneOpen: boolean
  activeInteraction: unknown | null
  inspectorRunId: string | null
  requestWorkplacePhone: (
    tab: WorkplacePhoneTab,
    destination?: WorkplacePhoneDestination,
  ) => void
}

export function canUseHqRoomAction(state: HqRoomActionState): boolean {
  return state.isPlaying && !state.isPaused && !state.isAgentTalkOpen &&
    !state.isWorkplacePhoneOpen && state.activeInteraction === null &&
    state.inspectorRunId === null
}

/**
 * Atomically hand world input to the existing phone before pointer lock exits.
 * The return value lets the shared modal router decide whether lower-priority
 * world handlers may observe the same key press.
 */
export function activateHqRoomAction(
  code: string,
  action: HqRoomAction | null,
  state: HqRoomActionState,
  exitPointerLock: (() => void) | undefined,
): boolean {
  if (code !== 'KeyE' || !action || !canUseHqRoomAction(state)) return false
  state.requestWorkplacePhone(action.tab, action.destination)
  exitPointerLock?.()
  return true
}
